import { useState, useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import clsx from 'clsx';

const PRIORITY_LABELS: Record<number, { label: string; color: string; bg: string }> = {
  0: { label: '普通', color: '#6b7280', bg: '#f3f4f6' },
  1: { label: '重要', color: '#f4a261', bg: '#fff8e1' },
  2: { label: '紧急', color: '#e63946', bg: '#ffebee' },
  3: { label: '紧急', color: '#e63946', bg: '#ffebee' },
};

export default function TodoPanel({ eventId }: { eventId: number }) {
  const { todos, loadTodos, addTodo, toggleTodo, deleteTodo } = useAppStore();
  const list = todos[eventId] || [];
  const [newTitle, setNewTitle] = useState('');
  const [newPriority, setNewPriority] = useState(0);
  const [newDueDate, setNewDueDate] = useState('');

  useEffect(() => {
    loadTodos(eventId);
  }, [eventId, loadTodos]);

  const pending = list.filter((t) => !t.completed);
  const done = list.filter((t) => t.completed);

  const handleAdd = async () => {
    const title = newTitle.trim();
    if (!title) return;
    await addTodo(eventId, title, newPriority, newDueDate || undefined);
    setNewTitle('');
    setNewPriority(0);
    setNewDueDate('');
  };

  const renderItem = (t: (typeof list)[number]) => {
    const p = PRIORITY_LABELS[t.priority] || PRIORITY_LABELS[0];
    return (
      <div
        key={t.id}
        className={clsx(
          'flex items-center gap-3 p-3 rounded-xl border transition-all',
          t.completed ? 'bg-green-50 border-green-200' : 'bg-white border-amber-100 hover:border-amber-300'
        )}
      >
        {/* 勾选 */}
        <button
          onClick={() => toggleTodo(t.id, eventId)}
          className={clsx(
            'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all',
            t.completed ? 'bg-green-400 border-green-400 text-white' : 'border-gray-300 hover:border-amber-400'
          )}
        >
          {t.completed && '✓'}
        </button>

        <span className="text-xl">📝</span>

        <div className="flex-1 min-w-0">
          <div className={clsx('font-medium text-sm', t.completed && 'line-through text-gray-400')}>
            {t.title}
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span
              className="text-xs px-1.5 py-0.5 rounded-full"
              style={{ color: p.color, background: p.bg }}
            >
              {p.label}
            </span>
            {t.dueDate && (
              <span className="text-xs text-gray-400">截止 {t.dueDate}</span>
            )}
          </div>
        </div>

        <button
          onClick={() => deleteTodo(t.id, eventId)}
          className="text-xs px-2 py-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"
          title="删除"
        >
          ✕
        </button>
      </div>
    );
  };

  return (
    <div id="panel-todo" className="ac-card p-5">
      <div id="todo-header" className="flex items-center gap-2 mb-4">
        <span className="text-2xl">📋</span>
        <h3 className="font-bold text-lg text-gray-800">待办清单</h3>
        <span className="text-xs text-gray-400 ml-auto">{pending.length} 件待办</span>
      </div>

      {/* 新增待办 */}
      <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-2 mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="新增待办事项..."
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            className="flex-1 text-sm px-3 py-2 rounded-lg border border-amber-200 bg-white"
          />
        </div>
        <div className="flex gap-2 items-center">
          <select
            value={newPriority}
            onChange={(e) => setNewPriority(Number(e.target.value))}
            className="text-sm px-2 py-1.5 rounded-lg border border-amber-200 bg-white"
          >
            <option value={0}>普通</option>
            <option value={1}>重要</option>
            <option value={2}>紧急</option>
          </select>
          <input
            type="date"
            value={newDueDate}
            onChange={(e) => setNewDueDate(e.target.value)}
            className="text-sm px-2 py-1.5 rounded-lg border border-amber-200 bg-white"
          />
          <button
            onClick={handleAdd}
            disabled={!newTitle.trim()}
            className={clsx(
              'text-sm px-4 py-1.5 rounded-lg text-white font-medium whitespace-nowrap',
              newTitle.trim() ? 'bg-red-500 hover:opacity-90' : 'bg-gray-300 cursor-not-allowed'
            )}
            style={newTitle.trim() ? { background: '#e63946' } : undefined}
          >
            添加
          </button>
        </div>
      </div>

      {/* 待办列表 */}
      <div className="space-y-2">
        {pending.length > 0 && (
          <>
            <p className="text-xs font-medium text-gray-500">待处理</p>
            {pending.map(renderItem)}
          </>
        )}
        {done.length > 0 && (
          <>
            <p className="text-xs font-medium text-gray-500 mt-3">已完成</p>
            {done.map(renderItem)}
          </>
        )}
        {list.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">暂无待办，新增一条开始筹办吧</p>
        )}
      </div>
    </div>
  );
}
